import { useEffect, useMemo, useState } from 'react';
import { ApiError, api, type GuestContractVersion, type GuestContractView } from '../api';
import { PageHeader } from '../components/PageHeader';
import { DocZone, type DocSource } from './DocView';
import { commentingNote, STATUS_LABEL, versionNo } from './registry';

/**
 * Ответ представителя УК по кругу словами — те же, что называет держателю
 * карточка (`contracts.view.ts`, `guest.service.ts`): без замечаний, либо
 * замечания оставлены в ветках текста.
 */
const UK_ANSWER_LABEL: Record<'OK' | 'REMARKS', string> = {
  OK: 'согласовано без замечаний',
  REMARKS: 'замечания в ветках',
};

/*
 * Договор глазами подрядчика: страница по ссылке, без учётной записи
 * (спека «Гостевая страница»). Открывают её чаще всего с телефона.
 *
 * Своей вёрстки у документа здесь нет: зона та же, что у держателя
 * (`DocZone`), а разница описана источником — сравнения версий нет, ответа
 * по существу нет, docx с примечаниями нет, внутренних веток гость не видит
 * вовсе. Что именно уходит наружу, решает сервер (guest.service.ts); экран
 * ничего не прячет задним числом, он просто не просит лишнего.
 *
 * Раскладка — оболочка приложения без оболочки: `.app > .wrap > .nav-page`.
 * Меню, полосы вкладок и колокольчика у гостя нет, но правило телефона то же
 * самое, и живёт оно в тех же классах: экран в высоту окна, прокрутка ровно
 * одна — `.nav-page`.
 */

export function GuestContract({ token }: { token: string }) {
  const [view, setView] = useState<GuestContractView | null>(null);
  const [versions, setVersions] = useState<GuestContractVersion[]>([]);
  // Ссылку присылают письмом, её легко обрезать, а держатель может её отозвать:
  // по мёртвому адресу должна быть видна причина, а не пустая страница.
  // «invite» — адрес жив, но без приглашения на почту сюда не войти (403).
  const [closed, setClosed] = useState<'' | 'invite' | 'dead'>('');
  // Мёртвой ссылку делает только отказ сервера: оборванная связь или пятисотка
  // говорят о сети, а не о ссылке, и «недействительна» было бы враньём —
  // подрядчик выбросил бы рабочий адрес.
  const [err, setErr] = useState('');
  // Ответ представителя УК по кругу (часть 4): пока он отправляется, кнопки
  // не жмутся дважды.
  const [ukBusy, setUkBusy] = useState(false);
  const answerUk = (a: 'OK' | 'REMARKS') => {
    setUkBusy(true);
    api
      .guestUkAnswer(token, a)
      .then(setView)
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Не удалось отправить ответ'))
      .finally(() => setUkBusy(false));
  };

  useEffect(() => {
    let alive = true;
    api
      .guestContract(token)
      .then((v) => {
        if (alive) setView(v);
      })
      .catch((e) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 403) setClosed('invite');
        else if (e instanceof ApiError && e.status === 404) setClosed('dead');
        else setErr(e instanceof ApiError ? e.message : 'Не удалось открыть договор: проверьте связь');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  // Версии просим, когда договор уже открылся: без приглашения сервер не пустит.
  const joined = !!view;
  useEffect(() => {
    if (!joined) return;
    let alive = true;
    api
      .guestVersions(token)
      .then((list) => {
        if (alive) setVersions(list);
      })
      .catch((e) => {
        if (alive && e instanceof ApiError && e.status === 404) setClosed('dead');
      });
    return () => {
      alive = false;
    };
  }, [token, joined]);

  const doc: DocSource | null = useMemo(() => {
    if (!view) return null;
    /*
     * Места подрядчик видит наши и нашими названиями: своего списка мест у
     * него нет, название приезжает с каждой версией. Порядок — порядок
     * появления в списке версий, то есть наш порядок мест.
     */
    const places: { id: string; title: string }[] = [];
    for (const v of versions) {
      if (v.placeId && !places.some((p) => p.id === v.placeId)) {
        places.push({ id: v.placeId, title: v.placeTitle });
      }
    }
    return {
      key: token,
      versions,
      places,
      statusLabel: STATUS_LABEL[view.status],
      round: view.round,
      canComment: view.status === 'COMMENTING',
      // Подрядчик видит ту же причину, что и сотрудник: между кругами страница
      // у него тоже читается и не правится, а пустая страница без полей
      // замечаний читается как сломанная ссылка.
      closedNote: commentingNote(view.status, view.round, true),
      holder: false,
      guest: true,
      pdfUrl: (versionId) => api.guestPdfUrl(token, versionId),
      exportUrl: () => '',
      onCompare: null,
      list: () => api.guestComments(token),
      add: (body) => api.addGuestComment(token, body),
      snapshot: null,
      mine: () => false,
    };
  }, [token, versions, view]);

  // Номер версии — тот же, что у держателя: её круг (registry.ts). Своей
  // нумерации у гостя нет — список версий у него обрезан рубежом ссылки, и
  // место в этом списке называло бы третью версию первой.
  const last = versions.filter((v) => v.hasPdf).at(-1);
  const shownNo = last ? versionNo(versions, last.id) : 0;

  return (
    <div className="app">
      <div className="wrap">
        <div className="nav-page">
          {closed === 'invite' && (
            <div className="empty">
              <b>Доступ к документу — по личному приглашению</b>
              Попросите сотрудника CODE прислать приглашение на вашу почту.
            </div>
          )}
          {closed === 'dead' && <div className="empty">Ссылка недействительна или закрыта</div>}
          {!closed && !!err && (
            <div className="banner crit">
              <b>{err}</b>
            </div>
          )}
          {!closed && !err && !view && <div className="load-screen">Загружаю договор…</div>}
          {!closed && view && doc && (
            <>
              <PageHeader
                title={view.subject}
                subtitle={[
                  `${view.legalEntity} и ${view.party}`,
                  view.side === 'UK' ? 'представитель УК' : '',
                  view.guest?.name,
                  shownNo ? `версия ${shownNo}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
                action={<span className="status active">Круг {view.round}</span>}
              />
              {/* Ответ представителя УК по кругу (часть 4): только по ссылке
                  УК и только пока круг идёт — держатель то же самое отмечает
                  сам, если УК ответила мимо ссылки. */}
              {view.side === 'UK' && view.status === 'COMMENTING' && (
                view.ukAnswer ? (
                  <div className="banner ok">
                    <b>
                      Ответ УК по кругу {view.round}: {UK_ANSWER_LABEL[view.ukAnswer]}
                    </b>
                  </div>
                ) : (
                  <div className="card">
                    <div className="cnt-head">
                      <h2>Ответ УК по кругу {view.round}</h2>
                    </div>
                    <div className="acts">
                      <button
                        className="btn primary"
                        type="button"
                        disabled={ukBusy}
                        onClick={() => answerUk('OK')}
                      >
                        Согласовать без замечаний
                      </button>
                      <button
                        className="btn outline"
                        type="button"
                        disabled={ukBusy}
                        onClick={() => answerUk('REMARKS')}
                      >
                        Замечания оставлены в ветках
                      </button>
                    </div>
                  </div>
                )
              )}
              <DocZone doc={doc} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
